// Two axios instances that share one auth-refresh lock so a single 401 in
// either instance triggers exactly one refresh attempt, and any concurrent
// failing requests resume against the new token once the refresh resolves.
//
// File shape (top → bottom mirrors a request's life):
//   constants                   → endpoint URL, status codes, queue type
//   refresh-queue helpers       → pushPending / resumePending / failPending
//   session helpers             → readToken / clearSessionAndRedirect
//   refresh runner              → refreshAccessToken
//   interceptor builders        → attachAuthHeader / handleResponseError
//   createApiInstance           → wire-up only, four lines of glue
//   exports                     → authApi + sellerApi

import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { config } from '@core/config/config'
import localStorageService from '@core/persistence/localStorage/localStorageService'
import { AUTH_STORAGE_KEYS } from '@core/persistence/storageKeys'
import { normalizeError } from '@core/api/normalizeError'

// ── Constants ────────────────────────────────────────────────────────────
const AUTH_REFRESH_PATH = '/api/auth/generate-access-token'
const HTTP_UNAUTHORIZED = 401
const LOGIN_PATH = '/login'

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean }

interface PendingRequest {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}

// ── Shared refresh-queue state ───────────────────────────────────────────
// Module-scoped so authApi + sellerApi cooperate: whichever instance
// sees the first 401 owns the refresh; the other queues until it resolves.
let isRefreshing = false
let pendingQueue: PendingRequest[] = []

const pushPending = (entry: PendingRequest): void => {
  pendingQueue.push(entry)
}

const resumePending = (newToken: string): void => {
  for (const entry of pendingQueue) entry.resolve(newToken)
  pendingQueue = []
}

const failPending = (error: unknown): void => {
  for (const entry of pendingQueue) entry.reject(error)
  pendingQueue = []
}

// ── Session helpers ──────────────────────────────────────────────────────
const readToken = (): string | null => localStorageService.getItem(AUTH_STORAGE_KEYS.TOKEN)
const readRefreshToken = (): string | null =>
  localStorageService.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)

const clearSessionAndRedirect = (): void => {
  localStorageService.removeItem(AUTH_STORAGE_KEYS.TOKEN)
  localStorageService.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
  window.location.href = LOGIN_PATH
}

// ── Refresh runner ───────────────────────────────────────────────────────
// Hits the auth-service refresh endpoint directly (NOT through authApi) so
// the response interceptor below can't recurse if the refresh itself 401s.
const refreshAccessToken = async (refreshToken: string): Promise<string> => {
  const { data } = await axios.post(`${config.authApiUrl}${AUTH_REFRESH_PATH}`, {
    refreshToken,
  })
  return data?.data?.accessToken ?? ''
}

// ── Interceptor builders ─────────────────────────────────────────────────
const attachAuthHeader = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = readToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
}

// Decides what to do with a non-2xx response. Most paths reject with an
// `AppError`; the 401 path tries to refresh once, queues concurrent 401s
// behind the in-flight refresh, and replays the original request on
// success — or drops the session and redirects on failure.
const buildResponseErrorHandler = (instance: AxiosInstance) => async (error: AxiosError) => {
  const originalRequest = error.config as RetryableRequest | undefined

  if (!originalRequest || error.response?.status !== HTTP_UNAUTHORIZED || originalRequest._retry) {
    return Promise.reject(normalizeError(error))
  }

  // A 401 on the refresh call itself means the refresh token is dead.
  if (originalRequest.url?.includes(AUTH_REFRESH_PATH)) {
    clearSessionAndRedirect()
    return Promise.reject(normalizeError(error))
  }

  // Another request is already refreshing — queue and replay when it lands.
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      pushPending({ resolve, reject })
    }).then((newToken) => {
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      return instance(originalRequest)
    })
  }

  originalRequest._retry = true
  isRefreshing = true

  const refreshToken = readRefreshToken()
  if (!refreshToken) {
    isRefreshing = false
    clearSessionAndRedirect()
    return Promise.reject(normalizeError(error))
  }

  try {
    const newToken = await refreshAccessToken(refreshToken)
    localStorageService.setItem(AUTH_STORAGE_KEYS.TOKEN, newToken)
    resumePending(newToken)
    originalRequest.headers.Authorization = `Bearer ${newToken}`
    return instance(originalRequest)
  } catch (refreshError) {
    failPending(refreshError)
    clearSessionAndRedirect()
    return Promise.reject(normalizeError(refreshError))
  } finally {
    isRefreshing = false
  }
}

// ── Factory ──────────────────────────────────────────────────────────────
const createApiInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({ baseURL })
  instance.interceptors.request.use(attachAuthHeader)
  instance.interceptors.response.use((response) => response, buildResponseErrorHandler(instance))
  return instance
}

export const authApi = createApiInstance(config.authApiUrl)
export const sellerApi = createApiInstance(config.sellerApiUrl)
// Anonymous instance for public (no-login) seller-service endpoints — e.g. the
// secure agreement-signing link an owner opens from email. It attaches NO auth
// header and deliberately skips the 401→refresh→/login handling above, which
// would hijack an anonymous flow. Errors are still normalised to AppError.
export const publicApi = axios.create({ baseURL: config.sellerApiUrl })
publicApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => Promise.reject(normalizeError(error)),
)
// Slicey AI conversation service. Reuses the same auth-refresh wiring so
// chat keeps working across token refreshes. `baseURL` may be empty in dev —
// the mutation surfaces a clean error if the user submits without it set.
export const sliceyApi = createApiInstance(config.sliceyApiUrl)

export default authApi
